import { PushNotificationServiceInterface } from 'domain/services';
import {
  PushNotificationUseCaseInterface,
  SchedulePushNotificationUseCaseInterface,
  ScheduledPushNotificationListUseCaseInterface,
  CancelScheduledPushNotificationUseCaseInterface,
  ScheduledPushNotificationGetByIdUseCaseInterface,
  UpdateScheduledPushNotificationUseCaseInterface,
  ReschedulePushNotificationUseCaseInterface,
  RecurringPushNotificationUseCaseInterface,
  RecurringPushNotificationListUseCaseInterface,
  RecurringPushNotificationGetByIdUseCaseInterface,
  UpdateRecurringPushNotificationUseCaseInterface,
  CancelRecurringPushNotificationUseCaseInterface,
  ResumeRecurringPushNotificationUseCaseInterface,
  PauseRecurringPushNotificationUseCaseInterface,
} from 'domain/usecases';
interface UseCasesInjection {
  pushNotification: PushNotificationUseCaseInterface;
  schedulePushNotification: SchedulePushNotificationUseCaseInterface;
  scheduledPushNotificationList: ScheduledPushNotificationListUseCaseInterface;
  cancelScheduledPushNotification: CancelScheduledPushNotificationUseCaseInterface;
  scheduledPushNotificationGetById: ScheduledPushNotificationGetByIdUseCaseInterface;
  updateScheduledPushNotification: UpdateScheduledPushNotificationUseCaseInterface;
  reschedulePushNotification: ReschedulePushNotificationUseCaseInterface;
  recurringPushNotification: RecurringPushNotificationUseCaseInterface;
  recurringPushNotificationList: RecurringPushNotificationListUseCaseInterface;
  recurringPushNotificationGetById: RecurringPushNotificationGetByIdUseCaseInterface;
  updateRecurringPushNotification: UpdateRecurringPushNotificationUseCaseInterface;
  cancelRecurringPushNotification: CancelRecurringPushNotificationUseCaseInterface;
  pauseRecurringPushNotification: PauseRecurringPushNotificationUseCaseInterface;
  resumeRecurringPushNotification: ResumeRecurringPushNotificationUseCaseInterface;
}

export class PushNotificationService
  implements PushNotificationServiceInterface
{
  private readonly _pushNotification: PushNotificationUseCaseInterface;
  private readonly _schedulePushNotification: SchedulePushNotificationUseCaseInterface;
  private readonly _scheduledPushNotificationList: ScheduledPushNotificationListUseCaseInterface;
  private readonly _cancelScheduledPushNotification: CancelScheduledPushNotificationUseCaseInterface;
  private readonly _scheduledPushNotificationGetById: ScheduledPushNotificationGetByIdUseCaseInterface;
  private readonly _updateScheduledPushNotification: UpdateScheduledPushNotificationUseCaseInterface;
  private readonly _reschedulePushNotification: ReschedulePushNotificationUseCaseInterface;
  private readonly _recurringPushNotification: RecurringPushNotificationUseCaseInterface;
  private readonly _recurringPushNotificationList: RecurringPushNotificationListUseCaseInterface;
  private readonly _recurringPushNotificationGetById: RecurringPushNotificationGetByIdUseCaseInterface;
  private readonly _updateRecurringPushNotification: UpdateRecurringPushNotificationUseCaseInterface;
  private readonly _cancelRecurringPushNotification: CancelRecurringPushNotificationUseCaseInterface;
  private readonly _pauseRecurringPushNotification: PauseRecurringPushNotificationUseCaseInterface;
  private readonly _resumeRecurringPushNotification: ResumeRecurringPushNotificationUseCaseInterface;

  constructor(usecases: UseCasesInjection) {
    this._pushNotification = usecases.pushNotification;
    this._schedulePushNotification = usecases.schedulePushNotification;
    this._scheduledPushNotificationList =
      usecases.scheduledPushNotificationList;
    this._cancelScheduledPushNotification =
      usecases.cancelScheduledPushNotification;
    this._scheduledPushNotificationGetById =
      usecases.scheduledPushNotificationGetById;
    this._updateScheduledPushNotification =
      usecases.updateScheduledPushNotification;
    this._reschedulePushNotification = usecases.reschedulePushNotification;
    this._recurringPushNotification = usecases.recurringPushNotification;
    this._recurringPushNotificationList =
      usecases.recurringPushNotificationList;
    this._recurringPushNotificationGetById =
      usecases.recurringPushNotificationGetById;
    this._updateRecurringPushNotification =
      usecases.updateRecurringPushNotification;
    this._cancelRecurringPushNotification =
      usecases.cancelRecurringPushNotification;
    this._pauseRecurringPushNotification =
      usecases.pauseRecurringPushNotification;
    this._resumeRecurringPushNotification =
      usecases.resumeRecurringPushNotification;
  }

  pushNotification(
    params: PushNotificationUseCaseInterface.Params,
  ): Promise<PushNotificationUseCaseInterface.Result> {
    return this._pushNotification.execute(params);
  }

  schedulePushNotification(
    params: SchedulePushNotificationUseCaseInterface.Params,
  ): Promise<SchedulePushNotificationUseCaseInterface.Result> {
    return this._schedulePushNotification.execute(params);
  }

  scheduledPushNotificationList(): Promise<ScheduledPushNotificationListUseCaseInterface.Result> {
    return this._scheduledPushNotificationList.execute();
  }

  cancelScheduledPushNotification(
    params: CancelScheduledPushNotificationUseCaseInterface.Params,
  ): Promise<CancelScheduledPushNotificationUseCaseInterface.Result> {
    return this._cancelScheduledPushNotification.execute(params);
  }

  scheduledPushNotificationGetById(
    params: ScheduledPushNotificationGetByIdUseCaseInterface.Params,
  ): Promise<ScheduledPushNotificationGetByIdUseCaseInterface.Result> {
    return this._scheduledPushNotificationGetById.execute(params);
  }

  updateScheduledPushNotification(
    params: UpdateScheduledPushNotificationUseCaseInterface.Params,
  ): Promise<UpdateScheduledPushNotificationUseCaseInterface.Result> {
    return this._updateScheduledPushNotification.execute(params);
  }

  reschedulePushNotification(
    params: ReschedulePushNotificationUseCaseInterface.Params,
  ): Promise<ReschedulePushNotificationUseCaseInterface.Result> {
    return this._reschedulePushNotification.execute(params);
  }

  recurringPushNotification(
    params: RecurringPushNotificationUseCaseInterface.Params,
  ): Promise<RecurringPushNotificationUseCaseInterface.Result> {
    return this._recurringPushNotification.execute(params);
  }

  recurringPushNotificationList(): Promise<RecurringPushNotificationListUseCaseInterface.Result> {
    return this._recurringPushNotificationList.execute();
  }

  recurringPushNotificationGetById: (
    params: RecurringPushNotificationGetByIdUseCaseInterface.Params,
  ) => Promise<RecurringPushNotificationGetByIdUseCaseInterface.Result>;

  updateRecurringPushNotification: (
    params: UpdateRecurringPushNotificationUseCaseInterface.Params,
  ) => Promise<UpdateRecurringPushNotificationUseCaseInterface.Result>;

  cancelRecurringPushNotification(
    params: CancelRecurringPushNotificationUseCaseInterface.Params,
  ): Promise<CancelRecurringPushNotificationUseCaseInterface.Result> {
    return this._cancelRecurringPushNotification.execute(params);
  }

  pauseRecurringPushNotification: (
    params: PauseRecurringPushNotificationUseCaseInterface.Params,
  ) => Promise<PauseRecurringPushNotificationUseCaseInterface.Result>;

  resumeRecurringPushNotification: (
    params: ResumeRecurringPushNotificationUseCaseInterface.Params,
  ) => Promise<ResumeRecurringPushNotificationUseCaseInterface.Result>;
}
